import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Star, MapPin, Clock, ShieldCheck, Languages, Undo2, Calendar, Truck, Award, CheckCircle2, XCircle, Phone, Accessibility, Navigation, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import NotifyPriceDropButton from '../components/NotifyPriceDropButton';

const qualityColor = { premium: 'bg-[#E8F5E9] text-[#1B5E20]', standard: 'bg-[#FFF3E0] text-[#9A4500]', economy: 'bg-muted text-ink/60' };

export default function Compare() {
  const [sp] = useSearchParams();
  const q = sp.get('q') || '';
  const [data, setData] = useState(null);
  const [loc, setLoc] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('mandispot_buyer_loc') || 'null');
      return cached || { lat: null, lng: null, pincode: '', source: null };
    } catch { return { lat: null, lng: null, pincode: '', source: null }; }
  });
  const [locating, setLocating] = useState(false);
  const [pinInput, setPinInput] = useState(loc.pincode || '');

  const fetchData = (locArg) => {
    if (!q) return;
    const params = { q };
    if (locArg?.lat != null && locArg?.lng != null) { params.lat = locArg.lat; params.lng = locArg.lng; }
    else if (locArg?.pincode) { params.pincode = locArg.pincode; }
    api.get('/compare', { params }).then(r => setData(r.data));
  };

  useEffect(() => { fetchData(loc); /* eslint-disable-next-line */ }, [q]);

  const useBrowserLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported on this device'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = { lat: pos.coords.latitude, lng: pos.coords.longitude, pincode: '', source: 'gps' };
        setLoc(next); localStorage.setItem('mandispot_buyer_loc', JSON.stringify(next));
        fetchData(next);
        setLocating(false);
        toast.success('Showing stores nearest to you');
      },
      (err) => { setLocating(false); toast.error(err.message || 'Could not get your location'); },
      { timeout: 8000, enableHighAccuracy: false }
    );
  };

  const applyPincode = () => {
    const v = (pinInput || '').replace(/\D/g, '');
    if (v.length !== 6) { toast.error('Enter a 6-digit PIN code'); return; }
    const next = { lat: null, lng: null, pincode: v, source: 'pincode' };
    setLoc(next); localStorage.setItem('mandispot_buyer_loc', JSON.stringify(next));
    fetchData(next);
    toast.success(`Showing stores nearest to PIN ${v}`);
  };

  const clearLoc = () => {
    const next = { lat: null, lng: null, pincode: '', source: null };
    setLoc(next); setPinInput(''); localStorage.removeItem('mandispot_buyer_loc');
    fetchData(next);
  };

  if (!q) return <div className="max-w-3xl mx-auto px-6 py-16 text-ink/60">No product specified. Try searching first.</div>;
  if (!data) return <div className="max-w-3xl mx-auto px-6 py-16 text-ink/60">Loading comparison…</div>;
  if (data.count === 0) return (
    <div className="max-w-3xl mx-auto px-6 py-16 text-center">
      <h1 className="font-heading font-black text-3xl">No matches for "{q}"</h1>
      <Link to="/search" className="btn-primary inline-flex mt-5">Back to search</Link>
    </div>
  );

  const minPrice = Math.min(...data.results.map(r => r.price));
  const maxTrust = Math.max(...data.results.map(r => r.store.trust_score || 0));
  const hasBuyerLoc = data.buyer_lat != null && data.buyer_lng != null;
  const nearestDist = hasBuyerLoc
    ? Math.min(...data.results.filter(r => r.distance_km != null).map(r => r.distance_km), Infinity)
    : null;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#D35400]">Side-by-side comparison</div>
          <h1 className="font-heading font-black text-3xl md:text-4xl mt-1">"{q}" — {data.count} options</h1>
          <p className="text-ink/70 mt-1 text-sm">Compare by price, quality, store rating, return policy, language, store history & accessibility.</p>
        </div>
        <Link to={`/search?q=${encodeURIComponent(q)}`} className="btn-outline">Back to search</Link>
      </div>

      {/* Smart price-drop alert CTA — customers only */}
      <div className="mt-4">
        <NotifyPriceDropButton productQuery={q} testId="compare-notify-price-drop-btn"/>
      </div>

      {/* Buyer-location bar: enables nearest-shop sorting + distance column */}
      <div data-testid="buyer-location-bar" className="mt-4 card-soft p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-[#0D47A1]">
          <Navigation className="w-4 h-4"/>Nearest shop
        </div>
        {hasBuyerLoc ? (
          <div className="text-sm text-ink/70">
            Sorted by distance from{' '}
            <b>{loc.source === 'gps' ? 'your current location' : `PIN ${loc.pincode || '—'}`}</b>
            {' · '}
            <button data-testid="clear-buyer-loc" onClick={clearLoc} className="text-[#D35400] font-semibold hover:underline">change</button>
          </div>
        ) : (
          <>
            <button
              data-testid="use-current-location-btn"
              onClick={useBrowserLocation}
              disabled={locating}
              className="btn-primary !py-2 !text-xs"
            >
              {locating ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Navigation className="w-3.5 h-3.5"/>}
              {locating ? 'Locating…' : 'Use my current location'}
            </button>
            <div className="text-xs text-ink/50">or</div>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white pl-3 pr-1 py-1">
              <MapPin className="w-3.5 h-3.5 text-[#0D47A1]"/>
              <input
                data-testid="buyer-pincode-input"
                value={pinInput}
                onChange={e => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={e => { if (e.key === 'Enter') applyPincode(); }}
                placeholder="6-digit PIN"
                inputMode="numeric"
                maxLength={6}
                className="outline-none text-sm bg-transparent py-1 w-28 font-mono tracking-wider"
              />
              <button data-testid="apply-buyer-pincode-btn" onClick={applyPincode} className="btn-primary !py-1.5 !px-3 !text-xs">Apply</button>
            </div>
          </>
        )}
      </div>

      {/* Comparison table on desktop, cards on mobile */}
      <div className="mt-6 overflow-x-auto -mx-4 md:mx-0">
        <div className="min-w-[760px] grid" style={{gridTemplateColumns: `200px repeat(${data.results.length}, minmax(220px, 1fr))`}}>
          {/* Header row */}
          <div className="p-3 border-b border-border text-xs uppercase tracking-[0.2em] font-bold text-ink/50">Store</div>
          {data.results.map(r => (
            <div key={`h-${r.product_id}`} className="p-3 border-b border-l border-border bg-white">
              <div className="flex items-center gap-2">
                <Link to={`/stores/${r.store.id}`} className="font-heading font-bold text-lg leading-tight text-ink hover:text-[#D35400]">{r.store.name}</Link>
                {r.store.verified && <span title="Verified store" className="inline-flex items-center text-[10px] font-bold text-[#1B5E20]"><CheckCircle2 className="w-3.5 h-3.5"/></span>}
              </div>
              <div className="text-xs text-ink/60 mt-0.5 inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{r.store.village}</div>
            </div>
          ))}

          {/* Price */}
          <Row label="Price">
            {data.results.map(r => (
              <Cell key={`p-${r.product_id}`}>
                <div className="flex items-baseline gap-2">
                  <div className="font-heading font-black text-2xl text-ink">₹{r.price}</div>
                  <span className="text-xs text-ink/50">/ {r.unit}</span>
                  {r.price === minPrice && <span className="pill !text-[10px] !bg-[#E8F5E9] !text-[#1B5E20]"><Award className="w-3 h-3"/>Best price</span>}
                </div>
              </Cell>
            ))}
          </Row>

          {/* Distance — only when buyer location is set */}
          {hasBuyerLoc && (
            <Row label="Distance">
              {data.results.map(r => (
                <Cell key={`dist-${r.product_id}`}>
                  {r.distance_km != null ? (
                    <div className="inline-flex items-center gap-2">
                      <Navigation className="w-3.5 h-3.5 text-[#0D47A1]"/>
                      <span data-testid={`compare-distance-${r.product_id}`} className="font-heading font-bold text-base tabular-nums">{r.distance_km} km</span>
                      {nearestDist != null && r.distance_km === nearestDist && (
                        <span className="pill !text-[10px] !bg-[#E3F2FD] !text-[#0D47A1]">Nearest</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-ink/40">Store location unknown</span>
                  )}
                </Cell>
              ))}
            </Row>
          )}

          {/* Quality */}
          <Row label="Quality">
            {data.results.map(r => (
              <Cell key={`q-${r.product_id}`}>
                <span className={`pill !text-[10px] capitalize ${qualityColor[r.quality_grade] || ''}`}>{r.quality_grade}</span>
                {r.brand && <div className="text-xs text-ink/60 mt-1">Brand: <b>{r.brand}</b></div>}
              </Cell>
            ))}
          </Row>

          {/* Availability */}
          <Row label="Availability">
            {data.results.map(r => (
              <Cell key={`av-${r.product_id}`}>
                {r.in_stock
                  ? <span className="inline-flex items-center gap-1 text-[#1B5E20] text-sm font-semibold"><CheckCircle2 className="w-4 h-4"/>In stock ({r.stock})</span>
                  : <span className="inline-flex items-center gap-1 text-destructive text-sm font-semibold"><XCircle className="w-4 h-4"/>Out of stock</span>
                }
              </Cell>
            ))}
          </Row>

          {/* Store rating */}
          <Row label="Store rating">
            {data.results.map(r => (
              <Cell key={`r-${r.product_id}`}>
                <span className="inline-flex items-center gap-1"><Star className="w-4 h-4 fill-[#F1C40F] text-[#F1C40F]"/><b>{r.store.rating?.toFixed?.(1) || '–'}</b><span className="text-xs text-ink/50">/ 5</span></span>
              </Cell>
            ))}
          </Row>

          {/* Trust */}
          <Row label="Owner trust">
            {data.results.map(r => (
              <Cell key={`t-${r.product_id}`}>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full ${r.store.trust_score >= 85 ? 'bg-[#1B5E20]' : r.store.trust_score >= 70 ? 'bg-[#E67E22]' : 'bg-destructive'}`} style={{width: `${r.store.trust_score || 0}%`}}/>
                  </div>
                  <span className="text-xs font-bold">{r.store.trust_score}%</span>
                  {r.store.trust_score === maxTrust && <span className="pill !text-[10px] !bg-[#E8F5E9] !text-[#1B5E20]"><ShieldCheck className="w-3 h-3"/>Most trusted</span>}
                </div>
              </Cell>
            ))}
          </Row>

          {/* Established */}
          <Row label="Store history">
            {data.results.map(r => (
              <Cell key={`e-${r.product_id}`}>
                {r.store.established_year ? (
                  <div className="inline-flex items-center gap-1 text-sm"><Calendar className="w-3.5 h-3.5 text-ink/50"/>Since <b>{r.store.established_year}</b><span className="text-xs text-ink/50 ml-1">({new Date().getFullYear() - r.store.established_year} yrs)</span></div>
                ) : <span className="text-xs text-ink/40">—</span>}
              </Cell>
            ))}
          </Row>

          {/* Languages */}
          <Row label="Languages spoken">
            {data.results.map(r => (
              <Cell key={`l-${r.product_id}`}>
                <div className="inline-flex items-center gap-1.5 flex-wrap">
                  <Languages className="w-3.5 h-3.5 text-ink/50"/>
                  {(r.store.languages || []).length === 0 ? <span className="text-xs text-ink/40">—</span> : (r.store.languages || []).map(l => (
                    <span key={l} className="pill !text-[10px] !py-0.5">{l}</span>
                  ))}
                </div>
              </Cell>
            ))}
          </Row>

          {/* Return policy */}
          <Row label="Return policy">
            {data.results.map(r => (
              <Cell key={`rp-${r.product_id}`}>
                <div className="text-xs text-ink/80 inline-flex items-start gap-1.5"><Undo2 className="w-3.5 h-3.5 text-ink/50 mt-0.5 shrink-0"/><span>{r.store.return_policy || 'Not specified'}</span></div>
                <div className="text-[10px] uppercase tracking-wider mt-1 font-bold">
                  {r.returnable ? <span className="text-[#1B5E20]">Item returnable</span> : <span className="text-ink/50">Not returnable</span>}
                </div>
              </Cell>
            ))}
          </Row>

          {/* Hours */}
          <Row label="Opening hours">
            {data.results.map(r => (
              <Cell key={`h-${r.product_id}`}>
                <div className="inline-flex items-start gap-1.5 text-xs text-ink/80"><Clock className="w-3.5 h-3.5 text-ink/50 mt-0.5"/><span>{r.store.hours || '—'}</span></div>
              </Cell>
            ))}
          </Row>

          {/* Accessibility */}
          <Row label="Accessibility">
            {data.results.map(r => (
              <Cell key={`a-${r.product_id}`}>
                <div className="inline-flex items-start gap-1.5 text-xs text-ink/80"><Accessibility className="w-3.5 h-3.5 text-ink/50 mt-0.5"/><span>{r.store.accessibility_notes || '—'}</span></div>
              </Cell>
            ))}
          </Row>

          {/* Delivery */}
          <Row label="Delivery">
            {data.results.map(r => (
              <Cell key={`d-${r.product_id}`}>
                <div className="text-sm"><Truck className="w-3.5 h-3.5 inline mr-1 text-ink/50"/>₹{r.store.delivery_fee} fee · Min ₹{r.store.min_order}</div>
              </Cell>
            ))}
          </Row>

          {/* Phone */}
          <Row label="Contact">
            {data.results.map(r => (
              <Cell key={`c-${r.product_id}`}>
                {r.store.phone ? <a href={`tel:${r.store.phone}`} className="inline-flex items-center gap-1 text-sm font-semibold text-[#D35400]"><Phone className="w-3.5 h-3.5"/>{r.store.phone}</a> : <span className="text-xs text-ink/40">—</span>}
              </Cell>
            ))}
          </Row>

          {/* CTA */}
          <div className="p-3"/>
          {data.results.map(r => (
            <div key={`cta-${r.product_id}`} className="p-3 border-l border-border">
              <Link to={`/stores/${r.store.id}`} data-testid={`compare-visit-${r.store.id}`} className="btn-primary w-full !py-2">Visit store</Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <>
      <div className="p-3 border-b border-border text-xs uppercase tracking-[0.18em] font-bold text-ink/60 bg-[#F6F4EE]">{label}</div>
      {children}
    </>
  );
}
function Cell({ children }) {
  return <div className="p-3 border-b border-l border-border bg-white">{children}</div>;
}
